import React, { Dispatch, MutableRefObject, SetStateAction, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { CallGroupApi } from '@frontend/api-call-group';
import { scopedFilterAndSort } from '@frontend/filters';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useScopedQuery } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Text, Table, InfoIcon, useTooltip, Chip, styles, SpinningLoader } from '@frontend/design-system';
import { CreateSoftphonePropTypes, inputFieldStyles, type CallGroupDevicesListType } from '..';
import { queryKeys } from '../../../../query-keys';
import { PhoneSettingsScopedLocationFilterMenu } from '../../../common/index-table';

type Props = CreateSoftphonePropTypes & {
  initialCallGroupSelections: MutableRefObject<Record<string, boolean>>;
  setSelectedCallGroups: Dispatch<SetStateAction<CallGroupDevicesListType[]>>;
  isMultiLocation: boolean;
};

const DEFAULT_MAX_DEVICES = 30;

export const FourthStep: React.FC<React.PropsWithChildren<Props>> = ({
  values,
  isMultiLocation,
  initialCallGroupSelections,
  setSelectedCallGroups,
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'create-new-softphone' });
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const childLocations = settingsTenantLocation?.children ?? [];
  const unifyLocations = childLocations.reduce((acc, loc) => {
    return {
      ...acc,
      [loc.id]: loc.name ?? '',
    };
  }, {} as { [id: string]: string });
  const isUnifyTenant = settingsTenantLocation?.locationType === 'unify';

  const { data: callGroups = [], isFetched } = useScopedQuery({
    queryKey: queryKeys.listCallGroups(tenantId),
    queryFn: () => CallGroupApi.listCallGroups({ tenantId }),
    select: (data) =>
      (data.callGroups ?? [])
        .filter((group) => !group.name.toLocaleLowerCase().includes('e911'))
        .map((group) => ({
          ...group,
          locationId: group.locationIds[0] ?? '',
        })),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    meta: {
      error: {
        message: 'FourthStep:listCallGroupsQuery | Failed to fetch call groups',
        data: { tenantId },
      },
    },
  });

  const filteredCallGroups = useMemo(() => {
    // NOTE: the schema callgroup List endpoint only adds the locationIds callgroup data for unify tenants (single
    // locations will have an empty array). So we only filter by location for unify tenants.
    const filtered = scopedFilterAndSort({
      data: callGroups,
      scopeIds: isUnifyTenant ? globalAvailableLocationIds : [],
      filteredScopeIds: isUnifyTenant ? filteredLocations : [],
      scopeIdAccessor: (callGroup) => [callGroup.locationId],
    });
    if (searchValue) {
      return filtered.filter((callGroup) =>
        callGroup.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
      );
    }

    return filtered;
  }, [searchValue, callGroups, globalAvailableLocationIds, filteredLocations, isUnifyTenant]);

  return (
    <>
      <Text>
        <Trans t={t} deviceName={values?.deviceName}>
          Assign <b>{values?.deviceName}</b> to any relevant call groups so that it will receive incoming calls to the
          office and ring with other devices.
        </Trans>
      </Text>
      <Table
        isLoading={!isFetched}
        data={!!filteredCallGroups?.length ? filteredCallGroups : []}
        emptyStateConfig={{
          header: t('No Call Groups'),
        }}
        isPaginated
        clientPaginationConfig={{
          showGoToPageField: false,
        }}
        isSelectable
        hasResponsiveColWidths
        rowSelectionConfig={{
          hideBulkSelection: true,
          onSelectionToggle: (_, selectedGroups, selectedRowIDs) => {
            setSelectedCallGroups(selectedGroups);
            initialCallGroupSelections.current = selectedRowIDs;
          },
          initialState: initialCallGroupSelections.current,
          isRowDisabled: (callGroup) => callGroup.devicesCount >= DEFAULT_MAX_DEVICES,
        }}
        hasGlobalSearch
        globalSearchConfig={{
          searchHandler: setSearchValue,
          placeholder: t('Search Call Group'),
          debounceDelay: 500,
          position: isMultiLocation ? 'right' : 'left',
        }}
        expandableRowComponent={(row) => <ExpandedCallGroupRow callGroupId={row.id} />}
        colConfig={[
          {
            Header: t('Call Group Name'),
            accessor: (callGroup) => callGroup,
            cellRenderer: (callGroup: CallGroupDevicesListType) => {
              const isDisabled = callGroup.devicesCount >= DEFAULT_MAX_DEVICES;
              return <CallGroupName name={callGroup.name} isDisabled={isDisabled} />;
            },
            id: 'callgroup-name',
            sortType: 'caseInsensitive',
            width: childLocations.length ? 150 : 200,
          },
          {
            Header: t('Devices'),
            id: 'num-of-devices',
            accessor: (callGroup) => callGroup,
            cellRenderer: (callGroup: CallGroupDevicesListType) => (
              <Text
                css={css`
                  color: inherit;
                `}
              >
                {t('{{count}} devices', { count: callGroup.devicesCount })}
              </Text>
            ),
            disableSortBy: true,
            width: childLocations.length ? 115 : 200,
          },
          {
            Header: t('Location'),
            id: 'location',
            accessor: (callGroup) => callGroup,
            cellRenderer: ({ locationId }: CallGroupDevicesListType) => (
              <Chip.SingleChip>{unifyLocations[locationId]}</Chip.SingleChip>
            ),
            disableSortBy: true,
            omit: !isMultiLocation,
            width: 150,
          },
        ]}
        customToolbarRender={
          settingsTenantLocation && settingsTenantLocation?.locationType === 'unify'
            ? () => (
                <PhoneSettingsScopedLocationFilterMenu
                  selectedOptions={filteredLocations}
                  onChange={setFilteredLocations}
                />
              )
            : undefined
        }
        wrapperStyle={css`
          height: ${theme.spacing(52)};
          margin-bottom: ${theme.spacing(1)};

          .table-toolbar {
            justify-content: space-between;
            align-items: start;
          }

          & > div:first-of-type div {
            ${!isMultiLocation && inputFieldStyles}
          }
        `}
      />
    </>
  );
};

const ExpandedCallGroupRow = ({ callGroupId }: { callGroupId: string }) => {
  const { t } = useTranslation('phone', { keyPrefix: 'create-new-softphone' });
  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.callGroup(callGroupId),
    queryFn: () => CallGroupApi.readCallGroup({ callGroupId }),
    staleTime: 5 * 60 * 1000,
    meta: {
      error: {
        message: 'ExpandedCallGroupRow:readCallGroupQuery | Failed to fetch call group details',
        data: { callGroupId },
      },
    },
  });

  const deviceNames = (data?.devices ?? []).map((device) => {
    if ('deskPhone' in device) return device.deskPhone.name;
    if ('softPhone' in device) return device.softPhone.name;
    if ('mobilePhone' in device) return device.mobilePhone.name;
    if ('forwardingNumber' in device) return device.forwardingNumber.name;
    return undefined;
  });

  return (
    <div
      css={css`
        width: 100%;
        padding: var(--fabric-space-gap-md) var(--fabric-space-gap-xl);
        display: flex;
        flex-direction: column;
        height: auto;
      `}
    >
      {isLoading ? (
        <SpinningLoader
          size='small'
          css={css`
            margin: var(--fabric-space-gap-sm) auto;
          `}
        />
      ) : (
        <>
          <Text
            size='medium'
            color='light'
            css={css`
              margin-bottom: var(--fabric-space-gap-sm);
            `}
          >
            {deviceNames.length > 0 ? t('Devices in Call Group') : t('No Devices Assigned Yet')}
          </Text>
          {deviceNames.map((name, idx) => (
            <Text
              key={idx}
              css={css`
                margin: var(--fabric-space-gap-xs) var(--fabric-space-gap-sm);
              `}
            >
              {name}
            </Text>
          ))}
        </>
      )}
    </div>
  );
};

const CallGroupName = ({ name, isDisabled }: { name: string; isDisabled?: boolean }) => {
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({
    placement: 'top',
  });

  return (
    <>
      <Text css={styles.truncate} {...triggerProps} color={isDisabled ? 'disabled' : 'default'}>
        {name}
      </Text>
      <Tooltip {...tooltipProps}>{name}</Tooltip>
      {isDisabled && <InfoIconToolTip />}
    </>
  );
};

const InfoIconToolTip = () => {
  const { t } = useTranslation('phone', { keyPrefix: 'create-new-softphone' });

  const { Tooltip, tooltipProps, triggerProps } = useTooltip({
    placement: 'top',
  });

  return (
    <>
      <div
        {...triggerProps}
        css={css`
          margin-left: var(--fabric-space-gap-sm);
        `}
      >
        <InfoIcon color='light' size={16} />
      </div>
      <Tooltip {...tooltipProps}>{t('This call group has reached the maximum of 30 devices')}</Tooltip>
    </>
  );
};
